import React, { type FC } from "react";

import { cx } from "~/tools/class-variance";

import { healthStateToColorToken } from "../../../../common/functions";
import type { HealthStateValue } from "../../../../common/openapi";
import { Widget, WidgetBody, WidgetHeader } from "../../../../components/_widget";
import { HealthStateBadge } from "../../../../components/badges/HealthStateBadge";
import { healthStateToBackgroundColor } from "../../../../components/badges/health-state-background-color";
import { Markdown } from "../../../../components/react/markdown/Markdown";
import { HelpPopupTooltip } from "../../../../components/react/tooltip/HelpPopupTooltip";
import { FormatViewTime } from "../../../../components/react/viewtime/FormatViewTime";

export interface MonitorStatusDrawerHealthStateProps {
	health: HealthStateValue;
	selectedTimestamp: number;
	triggeredTimeStamp: number;
	reason?: string;
}

export const MonitorStatusDrawerHealthState: FC<MonitorStatusDrawerHealthStateProps> = ({
	health,
	selectedTimestamp,
	triggeredTimeStamp,
	reason,
}) => (
	<Widget frameless>
		<WidgetHeader
			size="xl"
			title={
				<>
					Monitor status for <FormatViewTime time={selectedTimestamp} />
				</>
			}
		/>

		<WidgetBody card className="flex flex-row overflow-hidden text-grey-800">
			<StatusCard
				className="w-1/4 border-0 border-l-[3px] border-solid"
				style={{
					backgroundColor: healthStateToBackgroundColor(health),
					borderColor: `var(${healthStateToColorToken(health)})`,
				}}
				title="Health state"
				tooltip="This is the moment when the monitor triggered a new state based on the processed data."
			>
				<div className="inline-block">
					<HealthStateBadge state={health} />
				</div>
			</StatusCard>

			<StatusCard className="w-1/4 border-0 border-grey-300 border-r border-l border-solid" title="Triggered time">
				<FormatViewTime time={triggeredTimeStamp} />
			</StatusCard>

			<StatusCard className="w-2/4" title="Reason">
				{reason != null && reason !== "" ? (
					<Markdown text={reason} />
				) : (
					<span className="text-grey-600">No reason provided by the monitor</span>
				)}
			</StatusCard>
		</WidgetBody>
	</Widget>
);

const StatusCard: FC<
	React.PropsWithChildren<{
		title: string;
		tooltip?: string;
		style?: React.CSSProperties;
		className?: string;
	}>
> = ({ className, title, children, tooltip, ...rest }) => (
	<div className={cx("bg-white p-4", className)} {...rest}>
		<p className="mb-1 font-medium text-sm">
			{title}
			{tooltip && <HelpPopupTooltip className="ml-2" content={tooltip} />}
		</p>
		{children}
	</div>
);
